/**
 * OnboardingLayout - Centered layout shell for onboarding steps (no sidebar).
 */
import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { brandPalette } from '../../../theme/brandPalette';
import { config } from '../../../config';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  maxWidth?: number;
  showLogo?: boolean;
  /** Optional e.g. "2 of 3" - shown as small stepper below logo */
  stepLabel?: string;
}

export default function OnboardingLayout({
  children,
  maxWidth = 520,
  showLogo = true,
  stepLabel,
}: OnboardingLayoutProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isDark ? theme.palette.background.default : brandPalette.neutral[200],
        backgroundImage: isDark
          ? 'none'
          : `radial-gradient(ellipse 100% 55% at 50% -15%, ${alpha(brandPalette.primary[100], 0.28)}, transparent)`,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 2, md: 4 },
          pt: { xs: 3, sm: 4, md: 5 },
          // Generous bottom inset so CTAs clear viewport + iOS home indicator
          paddingBottom: { xs: 'calc(48px + env(safe-area-inset-bottom, 0px))', sm: 'calc(64px + env(safe-area-inset-bottom, 0px))' },
        }}
      >
        {showLogo && (
          <Box sx={{ mb: stepLabel ? 1.5 : 3, textAlign: 'center' }}>
            {/* Brand lockup */}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <img src={config.faviconUrl} alt={config.brandName} style={{ width: 32, height: 32, borderRadius: 7 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  letterSpacing: -0.4,
                  color: isDark ? 'text.primary' : brandPalette.neutral[800],
                }}
              >
                {config.brandName}
              </Typography>
            </Box>
          </Box>
        )}

        {stepLabel && (
          <Typography
            variant="caption"
            sx={{
              mb: 2.5,
              color: 'text.disabled',
              fontWeight: 500,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
            }}
          >
            {stepLabel}
          </Typography>
        )}

        <Box sx={{ width: '100%', maxWidth }}>{children}</Box>
      </Box>
    </Box>
  );
}
